/**
 * FineIndex - Asset Index Database
 * 
 * Static class providing Unity-like AssetDatabase functionality.
 * Works on top of @engine/vfs to track assets with persistent GUIDs,
 * importer settings, and fast indexed queries.
 * 
 * Content types and importers are registered externally - no hardcoded types.
 * 
 * @example
 * ```typescript
 * import { FineIndex } from '@engine/aid';
 * import { vfs } from '@engine/vfs';
 * 
 * // Register content types
 * FineIndex.registerContentType({
 *   id: 'image',
 *   name: 'Image',
 *   extensions: ['png', 'jpg', 'webp'],
 *   defaultImporter: 'texture',
 * });
 * 
 * // Register importers
 * FineIndex.registerImporter({
 *   id: 'texture',
 *   name: 'Texture Importer',
 *   supportedContentTypes: ['image'],
 *   defaultParams: { filterMode: 'point' },
 * });
 * 
 * // Initialize
 * await FineIndex.initialize(vfs, 'MyProject');
 * 
 * // Full scan with progress
 * await FineIndex.refresh('/**\/*', (progress) => {
 *   console.log(`${progress.phase}: ${progress.current}/${progress.total}`);
 * });
 * 
 * // Get GUID for path
 * const guid = await FineIndex.getGUID('/sprites/player.png');
 * ```
 * 
 * @module @engine/aid/FineIndex
 */

import type { VirtualFileSystem } from '@engine/vfs';
import { Logger } from '@engine/logger';
import { FineIndexDB, deleteFineIndexDB } from './FineIndexDB';
import {
  type IFineIndexDB,
  type AssetEntry,
  type AssetQuery,
  type AssetQueryResult,
  type AssetChange,
  type IndexStats,
  type RefreshResult,
  type ContentTypeDefinition,
  type ImporterDefinition,
  type ImportContext,
  type ImportResult,
  type ProgressCallback,
  type ProducedAsset,
  generateGUID,
  deriveProducedGuid,
  hashContent,
} from './types';
import {
  type AssetMeta,
  isMetaPath,
  assetPathForMeta,
  emitCanonicalToml,
  metaFromEntry,
  readMeta,
  writeMeta,
  deleteMeta,
  moveMeta,
} from './MetaFile';

// ============================================
// FINE INDEX STATIC CLASS
// ============================================

const log = Logger.create('FineIndex');

/** Factory function that creates an IFineIndexDB instance for a given db name. */
export type FineIndexDBFactory = (dbName: string) => IFineIndexDB;

export class FineIndex {
  private static db: IFineIndexDB | null = null;
  private static vfs: VirtualFileSystem | null = null;
  private static dbFactory: FineIndexDBFactory | null = null;
  
  // Content type & importer registries
  private static contentTypes = new Map<string, ContentTypeDefinition>();
  private static importers = new Map<string, ImporterDefinition>();
  private static extensionToContentType = new Map<string, string>();
  
  // Default settings provider (optional, for smart defaults based on file content)
  private static defaultSettingsProvider: ((filename: string, content: ArrayBuffer, mimeType?: string) => Promise<Record<string, unknown>>) | null = null;
  
  // Change listeners
  private static listeners = new Set<(changes: AssetChange[]) => void>();

  // ============================================
  // CONTENT TYPE REGISTRATION
  // ============================================

  /**
   * Register a content type
   */
  static registerContentType(definition: ContentTypeDefinition): void {
    log.debug('registerContentType:', definition.id, 'defaultImporter:', definition.defaultImporter);
    this.contentTypes.set(definition.id, definition);
    
    // Build extension lookup
    for (const ext of definition.extensions) {
      this.extensionToContentType.set(ext.toLowerCase(), definition.id);
    }
  }

  /**
   * Unregister a content type
   */
  static unregisterContentType(id: string): boolean {
    const def = this.contentTypes.get(id);
    if (!def) return false;
    
    // Remove extension mappings
    for (const ext of def.extensions) {
      if (this.extensionToContentType.get(ext.toLowerCase()) === id) {
        this.extensionToContentType.delete(ext.toLowerCase());
      }
    }
    
    return this.contentTypes.delete(id);
  }

  /**
   * Get all registered content types
   */
  static getContentTypes(): ContentTypeDefinition[] {
    return Array.from(this.contentTypes.values());
  }

  /**
   * Get content type by ID
   */
  static getContentType(id: string): ContentTypeDefinition | undefined {
    return this.contentTypes.get(id);
  }

  // ============================================
  // IMPORTER REGISTRATION
  // ============================================

  /**
   * Register an importer
   */
  static registerImporter(definition: ImporterDefinition): void {
    log.debug('registerImporter:', definition.id, 'for contentTypes:', definition.supportedContentTypes);
    this.importers.set(definition.id, definition);
  }

  /**
   * Unregister an importer
   */
  static unregisterImporter(id: string): boolean {
    return this.importers.delete(id);
  }

  /**
   * Get all registered importers
   */
  static getImporters(): ImporterDefinition[] {
    return Array.from(this.importers.values());
  }

  /**
   * Compute the DEFAULT import order per content type, topologically from importers' `dependsOn`
   * declarations — no hardcoded tier list. tier(ct) = 0 if it depends on nothing, else
   * 1 + max(tier(dep)). Content types with no importer (or in a dependency cycle) get 0. Callers
   * (e.g. a full reindex) sort assets ascending by tier so dependencies import before dependents;
   * users can override these numbers in Project Settings.
   */
  static getDefaultImportOrder(): Map<string, number> {
    // contentType → the content types it depends on (union across importers that support it).
    const deps = new Map<string, Set<string>>();
    for (const imp of this.importers.values()) {
      for (const ct of imp.supportedContentTypes) {
        const set = deps.get(ct) ?? new Set<string>();
        for (const d of imp.dependsOn ?? []) set.add(d);
        deps.set(ct, set);
      }
    }

    const tiers = new Map<string, number>();
    const visiting = new Set<string>();
    const tierOf = (ct: string): number => {
      const cached = tiers.get(ct);
      if (cached !== undefined) return cached;
      const d = deps.get(ct);
      if (!d || d.size === 0 || visiting.has(ct)) {
        tiers.set(ct, 0); // no deps, unknown type, or cycle → base tier
        return 0;
      }
      visiting.add(ct);
      let max = -1;
      for (const dep of d) max = Math.max(max, tierOf(dep));
      visiting.delete(ct);
      const tier = max + 1;
      tiers.set(ct, tier);
      return tier;
    };
    for (const ct of deps.keys()) tierOf(ct);
    return tiers;
  }
  
  /**
   * Set a provider function for smart default import settings.
   * The provider can inspect file content to return format-specific defaults.
   * 
   * @example
   * ```typescript
   * import { ContentPipeline } from '@engine/contentpipeline';
   * 
   * FineIndex.setDefaultSettingsProvider(
   *   async (filename, content, mimeType) => {
   *     return await ContentPipeline.getDefaultImportSettings(filename, content, mimeType);
   *   }
   * );
   * ```
   */
  static setDefaultSettingsProvider(
    provider: (filename: string, content: ArrayBuffer, mimeType?: string) => Promise<Record<string, unknown>>
  ): void {
    log.debug('setDefaultSettingsProvider called');
    this.defaultSettingsProvider = provider;
    log.debug('Provider registered:', !!this.defaultSettingsProvider);
  }
  
  /**
   * Clear the default settings provider
   */
  static clearDefaultSettingsProvider(): void {
    this.defaultSettingsProvider = null;
  }

  /**
   * Get importer by ID
   */
  static getImporter(id: string): ImporterDefinition | undefined {
    return this.importers.get(id);
  }

  /**
   * Get importers that support a content type
   */
  static getImportersForContentType(contentType: string): ImporterDefinition[] {
    return Array.from(this.importers.values()).filter(
      imp => imp.supportedContentTypes.includes(contentType)
    );
  }

  // ============================================
  // TYPE INFERENCE
  // ============================================

  /**
   * Infer content type from file path
   */
  static inferContentType(path: string): string {
    const ext = path.split('.').pop()?.toLowerCase() || '';
    return this.extensionToContentType.get(ext) ?? 'unknown';
  }

  /**
   * Get default importer for a content type
   */
  static getDefaultImporter(contentType: string): string {
    const def = this.contentTypes.get(contentType);
    log.trace('getDefaultImporter:', contentType, '→', def?.defaultImporter ?? 'default');
    log.trace('contentTypes size:', this.contentTypes.size);
    return def?.defaultImporter ?? 'default';
  }

  /**
   * Get default importer params
   */
  static getDefaultImporterParams(importerType: string): Record<string, unknown> {
    const def = this.importers.get(importerType);
    return def?.defaultParams ? { ...def.defaultParams } : {};
  }

  // ============================================
  // INITIALIZATION
  // ============================================

  /**
   * Set a custom DB factory for creating IFineIndexDB instances.
   * Must be called before `initialize()`. When not set, defaults to Dexie/IndexedDB.
   */
  static setDBFactory(factory: FineIndexDBFactory): void {
    this.dbFactory = factory;
  }

  /**
   * Initialize FineIndex with a VFS instance
   */
  static async initialize(vfs: VirtualFileSystem, projectId: string = 'default'): Promise<void> {
    if (this.db) {
      await this.shutdown();
    }

    if (!vfs.isConnected()) {
      throw new Error('VFS must be connected before initializing FineIndex');
    }

    this.vfs = vfs;
    const dbName = `FineIndex_${projectId}`;
    this.db = this.dbFactory
      ? this.dbFactory(dbName)
      : new FineIndexDB(dbName);
    
    await this.db.init();
    
    log.info(`Initialized for project: ${projectId}`);
  }

  /**
   * Shutdown and cleanup
   */
  static async shutdown(): Promise<void> {
    if (this.db) {
      await this.db.close();
      this.db = null;
    }
    this.vfs = null;
    this.listeners.clear();
  }

  /**
   * Check if FineIndex is initialized
   */
  static isInitialized(): boolean {
    return this.db !== null && this.db.isReady();
  }

  /**
   * Delete project's index database
   */
  static async deleteIndex(projectId: string): Promise<void> {
    await deleteFineIndexDB(`FineIndex_${projectId}`);
  }

  private static ensureInitialized(): void {
    if (!this.isInitialized()) {
      throw new Error('FineIndex not initialized. Call FineIndex.initialize() first.');
    }
  }

  /**
   * True when the path sits under a dot-directory (or is itself a dotfile):
   * .cache, .aid, .git, the tooling scratch dirs .tscheck/.shadercheck, and also
   * root git files (.gitignore/.gitattributes). These hold produced/scratch data
   * or config, never source assets — and never get `.meta` sidecars either.
   *
   * Also skips a root-level `build/` directory: the export pipeline writes standalone
   * builds to `<project>/build`, and indexing that output would sprinkle stray `.meta`
   * sidecars all over the generated bundle (and re-index on every export). It's already
   * in the project `.gitignore`. Matched only at the root so a nested content folder
   * that happens to be named "build" is still indexed.
   */
  private static isInSkippedLocation(path: string): boolean {
    const normalized = path.replace(/\\/g, '/');

    // Root-anchored output dir.
    const firstSeg = normalized.replace(/^\/+/, '').split('/')[0];
    if (firstSeg === 'build') return true;

    // Critically: tsgo writes type-check scratch to <project>/.tscheck/scripts/bhv_*.ts — without
    // this, the scan indexed those as real behaviours and each compile pass re-checked them, so the
    // editor surfaced cascading `bhv__.tscheck_scripts_bhv__…` "errors" against its own cache.
    return normalized.split('/').some((seg) => seg.length > 1 && seg.startsWith('.'));
  }

  /**
   * Check if a file should be skipped from indexing
   * (e.g., .keep files, .meta sidecars, cache files)
   */
  private static shouldSkipFile(path: string): boolean {
    if (path.endsWith('.keep') || path.endsWith('/.keep')) {
      return true;
    }
    // .meta sidecars carry asset identity — they are never assets themselves
    if (isMetaPath(path)) {
      return true;
    }
    return this.isInSkippedLocation(path);
  }

  // ============================================
  // .META SIDECARS (git source of truth for identity)
  // ============================================

  /**
   * Sync an entry's identity (guid, importerType, importerParams, labels) to its
   * `.meta` sidecar. Sidecars are the git-versioned source of truth; the DB is a
   * rebuildable cache of them, so every identity mutation must land here.
   *
   * - `ensure`: only materialize a sidecar when none (readable) exists — used on
   *   index/refresh so a user-edited sidecar is never clobbered by stale DB state.
   * - `sync`: overwrite semantics — used when the editor itself changed identity
   *   (import settings, labels, re-mint), i.e. the DB is ahead of the sidecar.
   */
  private static async persistMeta(entry: AssetEntry, mode: 'ensure' | 'sync'): Promise<void> {
    if (!this.vfs) return;
    // Produced assets (cache artifacts) and skipped locations never get sidecars
    if (entry.sourceGuid || this.shouldSkipFile(entry.path)) return;
    try {
      if (mode === 'ensure') {
        const existing = await readMeta(this.vfs, entry.path);
        if (existing) return;
      }
      await writeMeta(this.vfs, entry.path, metaFromEntry(entry));
    } catch (error) {
      log.warn('Failed to write meta for', entry.path, error);
    }
  }

  /** Semantic identity comparison between a sidecar and a DB entry. */
  private static metaMatchesEntry(meta: AssetMeta, entry: AssetEntry): boolean {
    return meta.guid === entry.guid
      && emitCanonicalToml(meta) === emitCanonicalToml(metaFromEntry(entry));
  }

  /**
   * Decide what a sidecar guid means for a path that has no DB entry yet.
   * - adopt: guid is free (or already ours) — use it
   * - move: guid belongs to an entry whose file is gone — this path is its rename
   *   (the git-side rename case: pull moved file+meta, identity must survive)
   * - remint: guid is claimed by another live asset (file copied with its meta) —
   *   the incumbent keeps it, this path needs a fresh guid
   */
  private static async resolveMetaGuid(
    path: string,
    metaGuid: string,
    pathExists: (p: string) => Promise<boolean>
  ): Promise<{ kind: 'adopt' } | { kind: 'move'; entry: AssetEntry } | { kind: 'remint'; takenBy: string }> {
    const existing = await this.db!.getByGuid(metaGuid);
    if (!existing || existing.path === path) return { kind: 'adopt' };
    // Produced/cache entries are engine-owned — a sidecar can never claim or move them
    if (existing.sourceGuid) return { kind: 'remint', takenBy: existing.path };
    if (await pathExists(existing.path)) return { kind: 'remint', takenBy: existing.path };
    return { kind: 'move', entry: existing };
  }

  /**
   * Apply a sidecar-detected rename: re-point the existing entry at the new path,
   * adopting the sidecar's identity fields. Import artifacts survive when the
   * content is unchanged (the common git-rename case), so no re-import is needed.
   */
  private static async applyMetaMove(
    entry: AssetEntry,
    newPath: string,
    meta: AssetMeta,
    file: { content: string | ArrayBuffer; size: number; updatedAt: Date }
  ): Promise<AssetEntry> {
    const newHash = hashContent(file.content);
    const invalidated = newHash !== entry.contentHash
      || emitCanonicalToml(meta) !== emitCanonicalToml(metaFromEntry({ ...entry, guid: meta.guid }));
    const updated: AssetEntry = {
      ...entry,
      path: newPath,
      contentType: this.inferContentType(newPath),
      importerType: meta.importerType,
      importerParams: meta.importerParams,
      labels: meta.labels,
      size: file.size,
      contentHash: newHash,
      fileModifiedAt: file.updatedAt,
      updatedAt: new Date(),
      importStatus: invalidated ? 'pending' : entry.importStatus,
      importError: invalidated ? undefined : entry.importError,
      artifacts: invalidated ? undefined : entry.artifacts,
      dependencies: invalidated ? undefined : entry.dependencies,
      importedData: invalidated ? undefined : entry.importedData,
    };
    await this.db!.put(updated);
    log.info(`Sidecar rename detected: ${entry.path} → ${newPath} (guid ${entry.guid} preserved)`);
    return updated;
  }

  // ============================================
  // GUID OPERATIONS
  // ============================================

  /**
   * Get GUID for a path. Creates new entry if doesn't exist, adopting identity
   * from the `.meta` sidecar when one is present (git clone / pull flows).
   */
  static async getGUID(path: string): Promise<string> {
    this.ensureInitialized();

    // Skip meta files
    if (this.shouldSkipFile(path)) {
      throw new Error(`Cannot index meta file: ${path}`);
    }

    // Check cache first
    let guid = this.db!.getGuidForPath(path);
    if (guid) return guid;

    // Check if file exists in VFS
    const file = await this.vfs!.readFile(path);
    if (!file) {
      throw new Error(`Asset not found: ${path}`);
    }

    // Adopt identity from the sidecar (git source of truth) when possible
    let adopted: AssetMeta | null = null;
    const meta = await readMeta(this.vfs!, path);
    if (meta) {
      const res = await this.resolveMetaGuid(path, meta.guid, (p) => this.vfs!.exists(p));
      if (res.kind === 'move') {
        const moved = await this.applyMetaMove(res.entry, path, meta, file);
        this.notifyListeners([{ type: 'moved', guid: moved.guid, path, previousPath: res.entry.path }]);
        return moved.guid;
      }
      if (res.kind === 'adopt') {
        adopted = meta;
      } else {
        log.warn(`Sidecar guid ${meta.guid} for ${path} is taken by ${res.takenBy} — minting a new guid`);
      }
    }

    // Create new entry
    guid = adopted?.guid ?? generateGUID();
    const contentType = this.inferContentType(path);
    const importerType = adopted?.importerType ?? this.getDefaultImporter(contentType);

    const entry: AssetEntry = {
      guid,
      path,
      contentType,
      importerType,
      importerParams: adopted?.importerParams ?? this.getDefaultImporterParams(importerType),
      size: file.size,
      contentHash: hashContent(file.content),
      createdAt: new Date(),
      updatedAt: new Date(),
      fileModifiedAt: file.updatedAt,
      labels: adopted?.labels ?? [],
      importStatus: 'pending',
    };

    await this.db!.put(entry);
    // 'sync' when the sidecar guid was re-minted (stale sidecar must be corrected),
    // 'ensure' otherwise (materialize a sidecar only if none exists yet)
    await this.persistMeta(entry, meta && !adopted ? 'sync' : 'ensure');

    return guid;
  }

  /**
   * Get GUID for path if exists (doesn't create new)
   */
  static getGUIDIfExists(path: string): string | null {
    this.ensureInitialized();
    return this.db!.getGuidForPath(path);
  }

  /**
   * Get path for a GUID
   */
  static getPath(guid: string): string | null {
    this.ensureInitialized();
    return this.db!.getPathForGuid(guid);
  }

  /**
   * Check if GUID exists
   */
  static hasGUID(guid: string): boolean {
    this.ensureInitialized();
    return this.db!.hasGuid(guid);
  }

  /**
   * Check if path exists in index
   */
  static hasPath(path: string): boolean {
    this.ensureInitialized();
    return this.db!.hasPath(path);
  }

  // ============================================
  // ASSET ENTRY OPERATIONS
  // ============================================

  /**
   * Get full asset entry by GUID
   */
  static async getEntry(guid: string): Promise<AssetEntry | null> {
    this.ensureInitialized();
    return this.db!.getByGuid(guid);
  }

  /**
   * Order assets so each imports AFTER the assets it depends on (the per-asset dependency DAG, from the
   * persisted `entry.dependencies` that importers record — e.g. a VFX → its textures, an atlas → its
   * source image). A `tieBreak` comparator orders independent assets (default: by the coarse importer
   * content-type tier passed by the caller). Dependencies outside the given set are ignored (already
   * imported / produced); cycle members are appended (not dropped).
   *
   * Scales to large projects: works on INTEGER node indices (not guid strings), holds the graph in
   * compact typed arrays (`Int32Array` indegree + CSR adjacency — no per-node JS arrays/Maps), and uses
   * a LAYERED Kahn so each node is sorted exactly once (one sort per dependency layer, never a re-sort
   * of the whole frontier). Extra memory ≈ 16·N + 4·E bytes + one guid→index map. Pure (no DB).
   */
  static sortByDependencies(
    entries: AssetEntry[],
    tieBreak: (a: AssetEntry, b: AssetEntry) => number = () => 0,
  ): AssetEntry[] {
    const n = entries.length;
    if (n < 2) return entries.slice();

    const indexOfGuid = new Map<string, number>();
    for (let i = 0; i < n; i++) indexOfGuid.set(entries[i].guid, i);

    // Two-pass CSR build of the dependents adjacency (dep → things that depend on it). `mark` dedups a
    // node's duplicate deps in O(1) without per-node Sets (so duplicates don't inflate the indegree).
    const indeg = new Int32Array(n);
    const outCount = new Int32Array(n);
    const mark = new Int32Array(n).fill(-1);
    for (let i = 0; i < n; i++) {
      const deps = entries[i].dependencies;
      if (!deps) continue;
      for (const d of deps) {
        const j = indexOfGuid.get(d);
        if (j === undefined || j === i || mark[j] === i) continue;
        mark[j] = i;
        indeg[i]++;
        outCount[j]++;
      }
    }
    const off = new Int32Array(n + 1);
    for (let i = 0; i < n; i++) off[i + 1] = off[i] + outCount[i];
    const adj = new Int32Array(off[n]); // total in-set edges
    const cursor = off.slice(0, n);
    mark.fill(-1);
    for (let i = 0; i < n; i++) {
      const deps = entries[i].dependencies;
      if (!deps) continue;
      for (const d of deps) {
        const j = indexOfGuid.get(d);
        if (j === undefined || j === i || mark[j] === i) continue;
        mark[j] = i;
        adj[cursor[j]++] = i; // i depends on j → i is a dependent of j
      }
    }

    const cmp = (a: number, b: number): number =>
      tieBreak(entries[a], entries[b]) || (entries[a].guid < entries[b].guid ? -1 : 1);

    const out: AssetEntry[] = [];
    let layer: number[] = [];
    for (let i = 0; i < n; i++) if (indeg[i] === 0) layer.push(i);
    while (layer.length > 0) {
      layer.sort(cmp); // sort just this dependency layer (each node sorted once, total ≤ O(N log N))
      const next: number[] = [];
      for (const i of layer) {
        out.push(entries[i]);
        for (let k = off[i]; k < off[i + 1]; k++) {
          const dependent = adj[k];
          if (--indeg[dependent] === 0) next.push(dependent);
        }
      }
      layer = next;
    }

    // Cycle (or self-referential data): nodes never reached 0 indegree → append in tie-break order.
    if (out.length < n) {
      const left: number[] = [];
      for (let i = 0; i < n; i++) if (indeg[i] > 0) left.push(i);
      left.sort(cmp);
      for (const i of left) out.push(entries[i]);
    }
    return out;
  }

  /**
   * Get full asset entry by path
   */
  static async getEntryByPath(path: string): Promise<AssetEntry | null> {
    this.ensureInitialized();
    return this.db!.getByPath(path);
  }

  /**
   * Update asset entry (preserves GUID and createdAt)
   */
  static async updateEntry(guid: string, updates: Partial<Omit<AssetEntry, 'guid' | 'createdAt'>>): Promise<AssetEntry> {
    this.ensureInitialized();

    const entry = await this.db!.getByGuid(guid);
    if (!entry) {
      throw new Error(`Asset not found: ${guid}`);
    }

    const updated: AssetEntry = {
      ...entry,
      ...updates,
      guid: entry.guid,
      createdAt: entry.createdAt,
      updatedAt: new Date(),
    };

    await this.db!.put(updated);
    return updated;
  }

  /**
   * Remove asset from index
   */
  static async remove(guid: string): Promise<boolean> {
    this.ensureInitialized();
    // Sidecar goes with the asset on explicit deletes (git-side deletes are
    // handled conservatively by refresh — see the deletion notes there)
    const entry = await this.db!.getByGuid(guid);
    if (entry && !entry.sourceGuid && this.vfs && !this.shouldSkipFile(entry.path)) {
      await deleteMeta(this.vfs, entry.path);
    }
    return this.db!.deleteByGuid(guid);
  }

  /**
   * Remove asset by path
   */
  static async removeByPath(path: string): Promise<boolean> {
    this.ensureInitialized();
    // Capture the guid BEFORE deleting (the path→guid mapping is gone afterwards) so we can notify.
    const guid = this.db!.getGuidForPath(path);
    if (this.vfs && !this.shouldSkipFile(path)) {
      await deleteMeta(this.vfs, path);
    }
    const ok = await this.db!.deleteByPath(path);
    // Notify listeners — without this, deleting via the UI/API removed the DB entry but never fired
    // onChange, so dependents (e.g. ScriptCompilationService) never recompiled and stale errors /
    // compiled outputs lingered for the deleted asset.
    if (ok && guid) {
      this.notifyListeners([{ type: 'deleted', guid, path }]);
    }
    return ok;
  }

  // ============================================
  // IMPORTER SETTINGS
  // ============================================

  /**
   * Set importer type for an asset
   */
  static async setImporterType(guid: string, importerType: string): Promise<void> {
    const updated = await this.updateEntry(guid, {
      importerType,
      importerParams: this.getDefaultImporterParams(importerType),
    });
    await this.persistMeta(updated, 'sync');
  }

  /**
   * Set importer parameters
   */
  static async setImporterParams(guid: string, params: Record<string, unknown>): Promise<void> {
    const updated = await this.updateEntry(guid, { importerParams: params });
    await this.persistMeta(updated, 'sync');
  }

  /**
   * Get importer parameters
   */
  static async getImporterParams(guid: string): Promise<Record<string, unknown> | null> {
    const entry = await this.getEntry(guid);
    return entry?.importerParams ?? null;
  }

  // ============================================
  // LABELS
  // ============================================

  /**
   * Add labels to an asset
   */
  static async addLabels(guid: string, labels: string[]): Promise<void> {
    const entry = await this.getEntry(guid);
    if (!entry) throw new Error(`Asset not found: ${guid}`);

    const newLabels = [...new Set([...entry.labels, ...labels])];
    const updated = await this.updateEntry(guid, { labels: newLabels });
    await this.persistMeta(updated, 'sync');
  }

  /**
   * Remove labels
   */
  static async removeLabels(guid: string, labels: string[]): Promise<void> {
    const entry = await this.getEntry(guid);
    if (!entry) throw new Error(`Asset not found: ${guid}`);

    const labelsToRemove = new Set(labels);
    const newLabels = entry.labels.filter(l => !labelsToRemove.has(l));
    const updated = await this.updateEntry(guid, { labels: newLabels });
    await this.persistMeta(updated, 'sync');
  }

  /**
   * Set all labels (replaces existing)
   */
  static async setLabels(guid: string, labels: string[]): Promise<void> {
    const updated = await this.updateEntry(guid, { labels: [...new Set(labels)] });
    await this.persistMeta(updated, 'sync');
  }

  // ============================================
  // PRODUCED ASSETS
  // ============================================

  /**
   * Register a produced/derived asset as a full AssetEntry.
   * The produced asset will have its own GUID and reference the source via sourceGuid.
   * 
   * @param sourceGuid - GUID of the source asset that produced this
   * @param path - Path where the produced asset is stored (typically in /.cache/)
   * @param contentType - Content type of the produced asset (e.g. 'compiled-texture', 'thumbnail')
   * @param options - Additional entry options
   * @returns The created AssetEntry
   */
  static async registerProducedEntry(
    sourceGuid: string,
    path: string,
    contentType: string,
    options: {
      size?: number;
      contentHash?: string;
      importedData?: Record<string, unknown>;
      metadata?: Record<string, unknown>;
    } = {}
  ): Promise<AssetEntry> {
    this.ensureInitialized();
    
    // Check if entry already exists for this path
    let entry = await this.db!.getByPath(path);
    
    // Merge metadata into importedData
    const combinedData = options.metadata || options.importedData
      ? { ...options.importedData, ...options.metadata }
      : undefined;
    
    if (entry) {
      // Update existing entry
      entry = {
        ...entry,
        sourceGuid,
        contentType,
        size: options.size ?? entry.size,
        contentHash: options.contentHash ?? entry.contentHash,
        importedData: combinedData ?? entry.importedData,
        updatedAt: new Date(),
        importStatus: 'imported',
      };
    } else {
      // Create new entry — DETERMINISTIC guid (v5-style hash of source+type), so
      // reimports and .cache wipes never re-mint produced identities
      let guid = deriveProducedGuid(sourceGuid, contentType);
      const clash = await this.db!.getByGuid(guid);
      if (clash && clash.path !== path) {
        // Same source+contentType already lives at another path (or a foreign entry
        // hash-collided) — disambiguate by path, still deterministic
        guid = deriveProducedGuid(sourceGuid, contentType, path);
      }
      const now = new Date();

      entry = {
        guid,
        path,
        contentType,
        importerType: 'none', // Produced assets don't need importers
        importerParams: {},
        size: options.size ?? 0,
        contentHash: options.contentHash ?? '',
        createdAt: now,
        updatedAt: now,
        fileModifiedAt: now,
        labels: [],
        importStatus: 'imported',
        sourceGuid,
        importedData: combinedData,
      };
    }
    
    await this.db!.put(entry);
    log.debug('registerProducedEntry:', sourceGuid, '→', path, `(${contentType})`);
    
    return entry;
  }

  /**
   * Add a produced asset to a source asset (legacy method - stores in producedAssets array).
   * If a produced asset with the same path already exists, it will be replaced.
   * @deprecated Use registerProducedEntry instead for proper asset tracking
   */
  static async addProducedAsset(sourceGuid: string, produced: Omit<ProducedAsset, 'producedAt'> & { producedAt?: Date }): Promise<void> {
    const entry = await this.getEntry(sourceGuid);
    if (!entry) throw new Error(`Asset not found: ${sourceGuid}`);
    
    const producedAssets = entry.producedAssets ? [...entry.producedAssets] : [];
    
    // Remove existing with same path
    const existingIndex = producedAssets.findIndex(p => p.path === produced.path);
    if (existingIndex >= 0) {
      producedAssets.splice(existingIndex, 1);
    }
    
    // Add new produced asset
    producedAssets.push({
      ...produced,
      producedAt: produced.producedAt ?? new Date(),
    });
    
    await this.updateEntry(sourceGuid, { producedAssets });
    log.debug('addProducedAsset:', sourceGuid, '→', produced.path, `(${produced.type})`);
  }

  /**
   * Get all produced assets for a source asset (legacy - returns ProducedAsset array from entry)
   */
  static async getProducedAssets(sourceGuid: string): Promise<ProducedAsset[]> {
    const entry = await this.getEntry(sourceGuid);
    return entry?.producedAssets ?? [];
  }

  /**
   * Get all produced asset entries for a source asset.
   * Returns full AssetEntry objects that have sourceGuid pointing to the given source.
   */
  static async getProducedEntries(sourceGuid: string): Promise<AssetEntry[]> {
    this.ensureInitialized();
    return this.db!.getBySourceGuid(sourceGuid);
  }

  /**
   * Clear all produced entries for a source asset.
   * Deletes all AssetEntry objects that have sourceGuid pointing to the given source.
   */
  static async clearProducedEntries(sourceGuid: string): Promise<void> {
    this.ensureInitialized();
    const entries = await this.db!.getBySourceGuid(sourceGuid);
    for (const entry of entries) {
      await this.db!.deleteByGuid(entry.guid);
    }
    log.debug('clearProducedEntries:', sourceGuid, `(${entries.length} entries)`);
  }

  /**
   * Clear ALL produced entries in the database.
   * Used during full reindex to remove stale produced assets.
   * @returns Number of entries deleted
   */
  static async clearAllProducedEntries(): Promise<number> {
    this.ensureInitialized();
    const count = await this.db!.deleteAllProducedEntries();
    log.debug('clearAllProducedEntries:', count, 'entries deleted');
    return count;
  }

  /**
   * Remove a produced asset by path
   */
  static async removeProducedAsset(sourceGuid: string, path: string): Promise<boolean> {
    const entry = await this.getEntry(sourceGuid);
    if (!entry || !entry.producedAssets) return false;
    
    const filtered = entry.producedAssets.filter(p => p.path !== path);
    if (filtered.length === entry.producedAssets.length) return false;
    
    await this.updateEntry(sourceGuid, { producedAssets: filtered });
    log.debug('removeProducedAsset:', sourceGuid, '→', path);
    return true;
  }

  /**
   * Clear all produced assets for a source asset
   */
  static async clearProducedAssets(sourceGuid: string): Promise<void> {
    await this.updateEntry(sourceGuid, { producedAssets: [] });
    log.debug('clearProducedAssets:', sourceGuid);
  }

  /**
   * Check if a produced asset exists
   */
  static async hasProducedAsset(sourceGuid: string, path: string): Promise<boolean> {
    const produced = await this.getProducedAssets(sourceGuid);
    return produced.some(p => p.path === path);
  }

  /**
   * Get a specific produced asset by path
   */
  static async getProducedAsset(sourceGuid: string, path: string): Promise<ProducedAsset | null> {
    const produced = await this.getProducedAssets(sourceGuid);
    return produced.find(p => p.path === path) ?? null;
  }

  /**
   * Get all produced assets of a specific type across all source assets
   */
  static async queryProducedByType(type: string): Promise<Array<{ sourceGuid: string; sourcePath: string; produced: ProducedAsset }>> {
    this.ensureInitialized();
    const result: Array<{ sourceGuid: string; sourcePath: string; produced: ProducedAsset }> = [];
    
    // Query all assets that have produced assets
    const allAssets = await this.db!.query({});
    for (const entry of allAssets.entries) {
      if (entry.producedAssets) {
        for (const produced of entry.producedAssets) {
          if (produced.type === type) {
            result.push({
              sourceGuid: entry.guid,
              sourcePath: entry.path,
              produced,
            });
          }
        }
      }
    }
    
    return result;
  }

  // ============================================
  // QUERIES
  // ============================================

  /**
   * Query assets with filters
   */
  static async query(query: AssetQuery): Promise<AssetQueryResult> {
    this.ensureInitialized();
    return this.db!.query(query);
  }

  /**
   * Get all assets of a content type
   */
  static async queryByContentType(type: string): Promise<AssetEntry[]> {
    this.ensureInitialized();
    return this.db!.getByContentType(type);
  }

  /**
   * Get all assets of an importer type
   */
  static async queryByImporterType(type: string): Promise<AssetEntry[]> {
    this.ensureInitialized();
    return this.db!.getByImporterType(type);
  }

  /**
   * Get all assets in a folder
   */
  static async queryByFolder(folderPath: string): Promise<AssetEntry[]> {
    const result = await this.query({ pathPrefix: folderPath });
    return result.entries;
  }

  /**
   * Get all assets with labels
   */
  static async queryByLabels(labels: string[]): Promise<AssetEntry[]> {
    const result = await this.query({ labels });
    return result.entries;
  }

  // ============================================
  // REFRESH / SYNC WITH VFS (with progress)
  // ============================================

  /**
   * Refresh index by scanning VFS for changes
   * 
   * @param pattern - Glob pattern to scan (default: all files)
   * @param onProgress - Progress callback for UI updates
   */
  static async refresh(pattern: string = '/**/*', onProgress?: ProgressCallback): Promise<RefreshResult> {
    this.ensureInitialized();
    
    const startTime = performance.now();
    const changes: AssetChange[] = [];
    
    // Phase 1: Scanning VFS
    onProgress?.({ current: 0, total: 0, phase: 'scanning', message: 'Scanning files...' });
    
    const allPaths = await this.vfs!.glob(pattern);
    // Filter out meta files (.keep)
    const vfsPaths = allPaths.filter(p => !this.shouldSkipFile(p));
    const vfsPathSet = new Set(vfsPaths);
    
    onProgress?.({ current: vfsPaths.length, total: vfsPaths.length, phase: 'scanning', message: `Found ${vfsPaths.length} files` });
    
    // Phase 2: Get indexed paths
    const indexedPaths = new Set(this.db!.getAllPaths());
    
    // Find new files
    const newPaths: string[] = [];
    for (const path of vfsPaths) {
      if (!indexedPaths.has(path)) {
        newPaths.push(path);
      }
    }
    
    // Find deleted files (candidates — a sidecar on a new path may reclaim one as a MOVE below)
    const deletionCandidates = new Map<string, string>(); // guid → old path
    for (const path of indexedPaths) {
      // Only protect PRODUCED cache assets here — they live in the DB but aren't scanned from VFS
      // (refresh regenerates, never deletes them). Do NOT use shouldSkipFile(): it also matches
      // scratch dot-dirs (.tscheck/.shadercheck), and we explicitly WANT stale entries from those
      // pruned once they drop out of the scan (otherwise tsgo-scratch phantoms linger forever).
      if (path.startsWith('/.cache/') || path.startsWith('.cache/')) continue;

      if (!vfsPathSet.has(path)) {
        const guid = this.db!.getGuidForPath(path);
        if (guid) {
          deletionCandidates.set(guid, path);
        }
      }
    }

    // Phase 3: Compare existing files for modifications + sidecar sync
    onProgress?.({ current: 0, total: indexedPaths.size, phase: 'comparing', message: 'Checking for changes...' });

    const modifiedEntries: AssetEntry[] = [];
    // Sidecar writes are deferred until after the DB bulk ops succeed
    const metaWrites: Array<{ entry: AssetEntry; mode: 'ensure' | 'sync' }> = [];
    // Guids claimed during THIS refresh (adoptions, moves, re-keys) — first claim wins
    const claimedGuids = new Set<string>();
    // Entries re-keyed to their sidecar guid: old row deleted, new row inserted
    const rekeyedOldGuids: string[] = [];
    let compareCount = 0;

    for (const path of [...indexedPaths].sort()) {
      if (vfsPathSet.has(path)) {
        const entry = await this.db!.getByPath(path);
        if (entry) {
          const file = await this.vfs!.readFile(path);
          if (file) {
            const newHash = hashContent(file.content);
            let updated: AssetEntry | null = null;
            if (newHash !== entry.contentHash) {
              updated = {
                ...entry,
                size: file.size,
                contentHash: newHash,
                fileModifiedAt: file.updatedAt,
                updatedAt: new Date(),
              };
            }

            // Sidecar sync (skip produced/scratch paths — they never have sidecars)
            if (!entry.sourceGuid && !this.shouldSkipFile(path)) {
              const meta = await readMeta(this.vfs!, path);
              if (!meta) {
                // Migration/self-heal: export the sidecar from the DB row. This is what
                // meta-ifies pre-sidecar projects on first open with this engine version.
                // 'ensure' (not 'sync'): if a git checkout lands a real sidecar between
                // now and phase 5, it must not be clobbered with stale DB state.
                metaWrites.push({ entry: updated ?? entry, mode: 'ensure' });
              } else if (meta.guid === entry.guid) {
                if (!this.metaMatchesEntry(meta, entry)) {
                  // Sidecar drifted (git pull changed settings) — sidecar wins, re-import
                  updated = {
                    ...(updated ?? entry),
                    importerType: meta.importerType,
                    importerParams: meta.importerParams,
                    labels: meta.labels,
                    updatedAt: new Date(),
                    importStatus: 'pending',
                    importError: undefined,
                    artifacts: undefined,
                    dependencies: undefined,
                    importedData: undefined,
                  };
                }
              } else if (!claimedGuids.has(meta.guid)) {
                // Sidecar carries a DIFFERENT guid for a path we know (git replaced the
                // sidecar). The sidecar is the source of truth: re-key when that guid is
                // free, otherwise keep ours and correct the sidecar.
                const res = await this.resolveMetaGuid(path, meta.guid, async (p) => vfsPathSet.has(p));
                if (res.kind === 'adopt') {
                  claimedGuids.add(meta.guid);
                  rekeyedOldGuids.push(entry.guid);
                  updated = {
                    ...(updated ?? entry),
                    guid: meta.guid,
                    importerType: meta.importerType,
                    importerParams: meta.importerParams,
                    labels: meta.labels,
                    updatedAt: new Date(),
                    importStatus: 'pending',
                    importError: undefined,
                    artifacts: undefined,
                    dependencies: undefined,
                    importedData: undefined,
                  };
                  changes.push({ type: 'deleted', guid: entry.guid, path });
                  changes.push({ type: 'added', guid: meta.guid, path });
                  log.warn(`Sidecar re-keyed ${path}: ${entry.guid} → ${meta.guid}`);
                } else {
                  log.warn(`Sidecar guid ${meta.guid} for ${path} is unavailable — keeping ${entry.guid} and rewriting the sidecar`);
                  metaWrites.push({ entry: updated ?? entry, mode: 'sync' });
                }
              }
            }

            if (updated) {
              modifiedEntries.push(updated);
              // Re-keys already pushed their own deleted+added pair
              if (updated.guid === entry.guid) {
                changes.push({ type: 'modified', guid: entry.guid, path });
              }
            }
          }
        }
      }

      compareCount++;
      if (compareCount % 50 === 0) {
        onProgress?.({ current: compareCount, total: indexedPaths.size, phase: 'comparing' });
      }
    }

    // Phase 4: Index new files (sorted for cross-machine determinism of collision handling)
    onProgress?.({ current: 0, total: newPaths.length, phase: 'indexing', message: 'Indexing new files...' });

    newPaths.sort();
    const newEntries: AssetEntry[] = [];

    for (let i = 0; i < newPaths.length; i++) {
      const path = newPaths[i];
      const file = await this.vfs!.readFile(path);

      if (file) {
        // Adopt identity from the sidecar (git source of truth) when possible
        // (newPaths are pre-filtered by shouldSkipFile, so every one may carry a sidecar)
        let adopted: AssetMeta | null = null;
        let reminted = false;
        const meta = await readMeta(this.vfs!, path);
        if (meta) {
          if (claimedGuids.has(meta.guid)) {
            // Two new sidecars share a guid (bad commit) — lexicographically-first path won
            log.warn(`Duplicate sidecar guid ${meta.guid} at ${path} — minting a new guid`);
            reminted = true;
          } else {
            const res = await this.resolveMetaGuid(path, meta.guid, async (p) => vfsPathSet.has(p));
            if (res.kind === 'move') {
              // Git-side rename: file+sidecar moved together. Convert delete+add → move,
              // preserving guid AND import artifacts (when content is unchanged).
              claimedGuids.add(meta.guid);
              deletionCandidates.delete(res.entry.guid);
              const newHash = hashContent(file.content);
              const invalidated = newHash !== res.entry.contentHash
                || emitCanonicalToml(meta) !== emitCanonicalToml(metaFromEntry({ ...res.entry, guid: meta.guid }));
              modifiedEntries.push({
                ...res.entry,
                path,
                contentType: this.inferContentType(path),
                importerType: meta.importerType,
                importerParams: meta.importerParams,
                labels: meta.labels,
                size: file.size,
                contentHash: newHash,
                fileModifiedAt: file.updatedAt,
                updatedAt: new Date(),
                importStatus: invalidated ? 'pending' : res.entry.importStatus,
                importError: invalidated ? undefined : res.entry.importError,
                artifacts: invalidated ? undefined : res.entry.artifacts,
                dependencies: invalidated ? undefined : res.entry.dependencies,
                importedData: invalidated ? undefined : res.entry.importedData,
              });
              changes.push({ type: 'moved', guid: res.entry.guid, path, previousPath: res.entry.path });
              log.info(`Sidecar rename detected: ${res.entry.path} → ${path} (guid ${res.entry.guid} preserved)`);
              continue;
            }
            if (res.kind === 'adopt') {
              claimedGuids.add(meta.guid);
              adopted = meta;
            } else {
              log.warn(`Sidecar guid ${meta.guid} for ${path} is taken by ${res.takenBy} — minting a new guid`);
              reminted = true;
            }
          }
        }

        const guid = adopted?.guid ?? generateGUID();
        const contentType = this.inferContentType(path);
        const importerType = adopted?.importerType ?? this.getDefaultImporter(contentType);

        const entry: AssetEntry = {
          guid,
          path,
          contentType,
          importerType,
          importerParams: adopted?.importerParams ?? this.getDefaultImporterParams(importerType),
          size: file.size,
          contentHash: hashContent(file.content),
          createdAt: new Date(),
          updatedAt: new Date(),
          fileModifiedAt: file.updatedAt,
          labels: adopted?.labels ?? [],
          importStatus: 'pending',
        };
        newEntries.push(entry);
        // Reminted sidecars are stale and must be corrected; fresh assets get theirs materialized
        metaWrites.push({ entry, mode: reminted ? 'sync' : 'ensure' });

        changes.push({ type: 'added', guid, path });
      }

      if ((i + 1) % 20 === 0 || i === newPaths.length - 1) {
        onProgress?.({ current: i + 1, total: newPaths.length, phase: 'indexing', currentItem: path });
      }
    }

    // Remaining deletion candidates are real deletions. Their sidecars are left on
    // disk INTENTIONALLY: a git checkout may materialize the sidecar before (or
    // without) its asset mid-refresh, and branch switches restore files later —
    // keeping the sidecar preserves identity in both cases. Explicit in-editor
    // deletes clean sidecars up via remove()/removeByPath().
    const deletedGuids = [...deletionCandidates.keys()];
    for (const [guid, path] of deletionCandidates) {
      changes.push({ type: 'deleted', guid, path });
    }

    // Phase 5: Apply changes
    onProgress?.({ current: 0, total: 3, phase: 'updating', message: 'Saving changes...' });

    // Re-keyed rows change their primary key: drop the old rows first
    if (rekeyedOldGuids.length > 0) {
      await this.db!.bulkDelete(rekeyedOldGuids);
    }
    if (deletedGuids.length > 0) {
      await this.db!.bulkDelete(deletedGuids);
    }
    onProgress?.({ current: 1, total: 3, phase: 'updating' });

    const allUpdates = [...modifiedEntries, ...newEntries];
    if (allUpdates.length > 0) {
      await this.db!.bulkPut(allUpdates);
    }

    // Sidecar writes go last — the DB state they mirror is committed now
    for (const { entry, mode } of metaWrites) {
      await this.persistMeta(entry, mode);
    }
    onProgress?.({ current: 2, total: 3, phase: 'updating' });
    
    // Update last refresh time
    await this.db!.setLastRefresh(new Date());
    onProgress?.({ current: 3, total: 3, phase: 'updating' });
    
    const duration = performance.now() - startTime;
    const stats = await this.db!.getStats();
    
    // Complete
    onProgress?.({ current: 1, total: 1, phase: 'complete', message: `Done: ${changes.length} changes` });
    
    // Notify listeners
    if (changes.length > 0) {
      this.notifyListeners(changes);
    }
    
    log.info(`Refresh: ${changes.length} changes in ${duration.toFixed(1)}ms`);
    
    return { changes, stats, duration };
  }

  /**
   * Index a single file (or update if exists)
   */
  static async indexFile(path: string, onProgress?: ProgressCallback): Promise<AssetEntry> {
    this.ensureInitialized();
    
    // Skip meta files
    if (this.shouldSkipFile(path)) {
      throw new Error(`Cannot index meta file: ${path}`);
    }
    
    onProgress?.({ current: 0, total: 1, phase: 'indexing', currentItem: path });

    const file = await this.vfs!.readFile(path);
    if (!file) {
      throw new Error(`File not found in VFS: ${path}`);
    }

    const existingEntry = await this.db!.getByPath(path);

    // Sidecar adoption for brand-new paths (git pull lands files via the watcher)
    let adopted: AssetMeta | null = null;
    let staleSidecar = false;
    if (!existingEntry) {
      const meta = await readMeta(this.vfs!, path);
      if (meta) {
        const res = await this.resolveMetaGuid(path, meta.guid, (p) => this.vfs!.exists(p));
        if (res.kind === 'move') {
          const moved = await this.applyMetaMove(res.entry, path, meta, file);
          onProgress?.({ current: 1, total: 1, phase: 'complete', currentItem: path });
          this.notifyListeners([{ type: 'moved', guid: moved.guid, path, previousPath: res.entry.path }]);
          return moved;
        }
        if (res.kind === 'adopt') {
          adopted = meta;
        } else {
          log.warn(`Sidecar guid ${meta.guid} for ${path} is taken by ${res.takenBy} — minting a new guid`);
          staleSidecar = true;
        }
      }
    }

    const guid = existingEntry?.guid ?? adopted?.guid ?? generateGUID();
    const contentType = this.inferContentType(path);
    // Update importerType when contentType changed (e.g., AID indexed as 'unknown',
    // then a subsystem registered the real content type)
    const contentTypeChanged = existingEntry != null && existingEntry.contentType !== contentType;
    const importerType = adopted?.importerType ?? ((contentTypeChanged || !existingEntry)
      ? this.getDefaultImporter(contentType)
      : existingEntry.importerType);

    log.trace('indexFile:', path, 'existingEntry:', !!existingEntry);
    log.trace('contentType:', contentType, 'importerType:', importerType);

    // Determine if content changed (needs re-import)
    const newHash = hashContent(file.content);
    const contentChanged = !existingEntry || existingEntry.contentHash !== newHash;
    log.trace('contentChanged:', contentChanged);

    // Get default importer params (a committed sidecar always wins)
    let importerParams = adopted?.importerParams ?? existingEntry?.importerParams;
    log.trace('existingEntry.importerParams:', importerParams);

    // Check if importerParams is empty or missing
    const hasParams = importerParams && Object.keys(importerParams).length > 0;
    
    if (!hasParams) {
      // Try smart defaults from provider first (content-aware)
      if (this.defaultSettingsProvider && file.content instanceof ArrayBuffer) {
        log.trace('Calling smart defaults provider for:', path);
        try {
          const smartDefaults = await this.defaultSettingsProvider(
            path.split('/').pop() ?? path,
            file.content,
            file.mimeType
          );
          log.trace('Smart defaults received:', smartDefaults);
          // Merge smart defaults with importer defaults
          const importerDefaults = this.getDefaultImporterParams(importerType);
          importerParams = { ...importerDefaults, ...smartDefaults };
          log.trace('Merged importer params:', importerParams);
        } catch (e) {
          log.warn('Failed to get smart defaults:', e);
          importerParams = this.getDefaultImporterParams(importerType);
        }
      } else {
        // Fallback to basic importer defaults
        log.trace('No provider or not ArrayBuffer, using default importer params');
        importerParams = this.getDefaultImporterParams(importerType);
      }
    } else {
      log.trace('Using existing importerParams from entry');
    }
    
    // Check if params were updated (need to re-import with new settings)
    const paramsChanged = !hasParams; // If params were empty/missing, they were just populated
    log.trace('paramsChanged:', paramsChanged);
    
    const entry: AssetEntry = {
      guid,
      path,
      contentType,
      importerType,
      importerParams,
      size: file.size,
      contentHash: newHash,
      createdAt: existingEntry?.createdAt ?? new Date(),
      updatedAt: new Date(),
      fileModifiedAt: file.updatedAt,
      labels: adopted?.labels ?? existingEntry?.labels ?? [],
      importStatus: (contentChanged || paramsChanged || contentTypeChanged) ? 'pending' : (existingEntry?.importStatus ?? 'pending'),
      importError: (contentChanged || paramsChanged || contentTypeChanged) ? undefined : existingEntry?.importError,
      artifacts: (contentChanged || paramsChanged || contentTypeChanged) ? undefined : existingEntry?.artifacts,
      dependencies: (contentChanged || paramsChanged || contentTypeChanged) ? undefined : existingEntry?.dependencies,
      importedData: (contentChanged || paramsChanged || contentTypeChanged) ? undefined : existingEntry?.importedData,
    };

    await this.db!.put(entry);
    // 'sync' when a stale sidecar guid was re-minted; 'ensure' otherwise so a
    // freshly pulled sidecar is never clobbered before its meta event lands
    await this.persistMeta(entry, staleSidecar ? 'sync' : 'ensure');

    onProgress?.({ current: 1, total: 1, phase: 'complete', currentItem: path });

    const changeType = existingEntry ? 'modified' : 'added';
    this.notifyListeners([{ type: changeType, guid, path }]);

    return entry;
  }

  /**
   * Bulk index multiple files
   */
  static async indexFiles(paths: string[], onProgress?: ProgressCallback): Promise<AssetEntry[]> {
    this.ensureInitialized();

    const entries: AssetEntry[] = [];
    const changes: AssetChange[] = [];
    const metaWrites: Array<{ entry: AssetEntry; mode: 'ensure' | 'sync' }> = [];
    const claimedGuids = new Set<string>();

    // Sorted for deterministic sidecar-collision handling (mirrors refresh)
    const sortedPaths = [...paths].sort();
    for (let i = 0; i < sortedPaths.length; i++) {
      const path = sortedPaths[i];

      // Skip meta files
      if (this.shouldSkipFile(path)) continue;

      onProgress?.({ current: i, total: sortedPaths.length, phase: 'indexing', currentItem: path });

      const file = await this.vfs!.readFile(path);
      if (!file) continue;

      const existingEntry = await this.db!.getByPath(path);

      // Sidecar adoption for brand-new paths
      let adopted: AssetMeta | null = null;
      let staleSidecar = false;
      if (!existingEntry) {
        const meta = await readMeta(this.vfs!, path);
        if (meta) {
          if (claimedGuids.has(meta.guid)) {
            log.warn(`Duplicate sidecar guid ${meta.guid} at ${path} — minting a new guid`);
            staleSidecar = true;
          } else {
            const res = await this.resolveMetaGuid(path, meta.guid, (p) => this.vfs!.exists(p));
            if (res.kind === 'move') {
              claimedGuids.add(meta.guid);
              const moved = await this.applyMetaMove(res.entry, path, meta, file);
              entries.push(moved);
              changes.push({ type: 'moved', guid: moved.guid, path, previousPath: res.entry.path });
              continue;
            }
            if (res.kind === 'adopt') {
              claimedGuids.add(meta.guid);
              adopted = meta;
            } else {
              log.warn(`Sidecar guid ${meta.guid} for ${path} is taken by ${res.takenBy} — minting a new guid`);
              staleSidecar = true;
            }
          }
        }
      }

      const guid = existingEntry?.guid ?? adopted?.guid ?? generateGUID();
      const contentType = this.inferContentType(path);
      const importerType = adopted?.importerType ?? existingEntry?.importerType ?? this.getDefaultImporter(contentType);

      const newHash = hashContent(file.content);
      const contentChanged = !existingEntry || existingEntry.contentHash !== newHash;

      const entry: AssetEntry = {
        guid,
        path,
        contentType,
        importerType,
        importerParams: adopted?.importerParams ?? existingEntry?.importerParams ?? this.getDefaultImporterParams(importerType),
        size: file.size,
        contentHash: newHash,
        createdAt: existingEntry?.createdAt ?? new Date(),
        updatedAt: new Date(),
        fileModifiedAt: file.updatedAt,
        labels: adopted?.labels ?? existingEntry?.labels ?? [],
        importStatus: contentChanged ? 'pending' : (existingEntry?.importStatus ?? 'pending'),
        importError: contentChanged ? undefined : existingEntry?.importError,
        artifacts: contentChanged ? undefined : existingEntry?.artifacts,
        dependencies: contentChanged ? undefined : existingEntry?.dependencies,
        importedData: contentChanged ? undefined : existingEntry?.importedData,
      };

      entries.push(entry);
      metaWrites.push({ entry, mode: staleSidecar ? 'sync' : 'ensure' });
      changes.push({ type: existingEntry ? 'modified' : 'added', guid, path });
    }

    if (entries.length > 0) {
      onProgress?.({ current: 0, total: entries.length, phase: 'updating', message: 'Saving...' });
      await this.db!.bulkPut(entries, onProgress);
    }

    for (const { entry, mode } of metaWrites) {
      await this.persistMeta(entry, mode);
    }

    onProgress?.({ current: 1, total: 1, phase: 'complete', message: `Indexed ${entries.length} files` });

    if (changes.length > 0) {
      this.notifyListeners(changes);
    }

    return entries;
  }

  /**
   * Handle file rename/move (preserves GUID)
   */
  static async moveAsset(oldPath: string, newPath: string): Promise<void> {
    this.ensureInitialized();

    const entry = await this.db!.getByPath(oldPath);
    if (!entry) {
      throw new Error(`Asset not found: ${oldPath}`);
    }

    const newContentType = this.inferContentType(newPath);

    const updated: AssetEntry = {
      ...entry,
      path: newPath,
      contentType: newContentType,
      updatedAt: new Date(),
    };

    await this.db!.put(updated);

    // The sidecar travels with its asset (raw move preserves user comments,
    // persistMeta then materializes one if the asset never had a sidecar)
    if (!entry.sourceGuid && !this.shouldSkipFile(newPath)) {
      await moveMeta(this.vfs!, oldPath, newPath);
      await this.persistMeta(updated, 'ensure');
    }

    this.notifyListeners([{
      type: 'moved',
      guid: entry.guid,
      path: newPath,
      previousPath: oldPath,
    }]);
  }

  /**
   * React to a `.meta` sidecar changing ON DISK (git pull, hand edit, external
   * tool). The sidecar is the source of truth for identity: drift is adopted into
   * the index (marking the asset pending for re-import), a deleted sidecar is
   * re-exported from the DB, and a guid change re-keys the entry when possible.
   * Echoes of our own sidecar writes no-op here (state already matches).
   *
   * @param path - sidecar path or its asset path
   * @returns the up-to-date entry, or null when there is nothing to sync
   */
  static async syncMeta(path: string): Promise<AssetEntry | null> {
    this.ensureInitialized();

    const assetPath = isMetaPath(path) ? assetPathForMeta(path) : path;
    if (this.isInSkippedLocation(assetPath) || assetPath.endsWith('.keep')) return null;

    const entry = await this.db!.getByPath(assetPath);

    if (!entry) {
      // Asset not indexed yet — if its file exists, index it (adoption reads the sidecar).
      // A sidecar without an asset is left alone: the file may still be materializing
      // (mid-checkout) and the sidecar preserves identity until it arrives.
      if (await this.vfs!.exists(assetPath)) {
        return this.indexFile(assetPath);
      }
      return null;
    }

    const meta = await readMeta(this.vfs!, assetPath);

    if (!meta) {
      // Sidecar deleted (or corrupted) externally. Self-heal from the DB — but only
      // while the asset file itself still exists: when git removes file+sidecar
      // together, resurrecting the sidecar would leave dust in the working tree.
      if (!(await this.vfs!.exists(assetPath))) return null;
      await this.persistMeta(entry, 'sync');
      return entry;
    }

    if (meta.guid === entry.guid) {
      if (this.metaMatchesEntry(meta, entry)) return entry; // our own write echo — no-op

      // Settings/labels drift: sidecar wins, re-import with the new settings
      const updated = await this.updateEntry(entry.guid, {
        importerType: meta.importerType,
        importerParams: meta.importerParams,
        labels: meta.labels,
        importStatus: 'pending',
        importError: undefined,
        artifacts: undefined,
        dependencies: undefined,
        importedData: undefined,
      });
      this.notifyListeners([{ type: 'modified', guid: entry.guid, path: assetPath }]);
      return updated;
    }

    // Guid changed via git — re-key to the sidecar guid when it's free
    const res = await this.resolveMetaGuid(assetPath, meta.guid, (p) => this.vfs!.exists(p));
    if (res.kind === 'adopt') {
      const rekeyed: AssetEntry = {
        ...entry,
        guid: meta.guid,
        importerType: meta.importerType,
        importerParams: meta.importerParams,
        labels: meta.labels,
        updatedAt: new Date(),
        importStatus: 'pending',
        importError: undefined,
        artifacts: undefined,
        dependencies: undefined,
        importedData: undefined,
      };
      await this.db!.deleteByGuid(entry.guid);
      await this.db!.put(rekeyed);
      log.warn(`Sidecar re-keyed ${assetPath}: ${entry.guid} → ${meta.guid}`);
      this.notifyListeners([
        { type: 'deleted', guid: entry.guid, path: assetPath },
        { type: 'added', guid: meta.guid, path: assetPath },
      ]);
      return rekeyed;
    }

    // Sidecar guid unavailable (claimed by a live asset) — keep ours, correct the sidecar
    log.warn(`Sidecar guid ${meta.guid} for ${assetPath} is unavailable — keeping ${entry.guid} and rewriting the sidecar`);
    await this.persistMeta(entry, 'sync');
    return entry;
  }

  // ============================================
  // IMPORT OPERATIONS
  // ============================================

  /**
   * Import a single asset using its registered importer
   * 
   * @param guid - Asset GUID to import
   * @param force - Force re-import even if already imported
   * @returns Import result
   */
  static async importAsset(guid: string, force: boolean = false): Promise<ImportResult> {
    this.ensureInitialized();
    
    log.trace('importAsset called:', guid, 'force:', force);
    const entry = await this.db!.getByGuid(guid);
    if (!entry) {
      log.error('Asset not found:', guid);
      return { success: false, error: `Asset not found: ${guid}` };
    }
    
    log.trace('Entry importStatus:', entry.importStatus);
    
    // Skip if already imported (unless forced)
    if (!force && entry.importStatus === 'imported') {
      log.trace('Asset already imported, skipping');
      return { 
        success: true, 
        artifacts: entry.artifacts,
        dependencies: entry.dependencies,
        importedData: entry.importedData,
      };
    }
    
    // Get importer
    const importer = this.importers.get(entry.importerType);
    log.trace('Importer:', entry.importerType, 'found:', !!importer);
    if (!importer?.import) {
      log.debug(`No importer found for type: ${entry.importerType}, ${entry.path}. Marking as imported with no artifacts.`);
      // No importer callback - mark as imported with no artifacts
      await this.updateEntry(guid, { 
        importStatus: 'imported',
        importError: undefined,
      });
      return { success: true };
    }
    
    // Read file content
    const file = await this.vfs!.readFile(entry.path);
    if (!file) {
      const error = `File not found: ${entry.path}`;
      await this.updateEntry(guid, { 
        importStatus: 'failed',
        importError: error,
      });
      return { success: false, error };
    }
    
    // Track produced assets during import
    const producedAssets: ProducedAsset[] = [];
    
    // Create import context
    const context: ImportContext = {
      path: entry.path,
      guid: entry.guid,
      contentType: entry.contentType,
      content: file.content,
      mimeType: file.mimeType,
      size: file.size,
      params: entry.importerParams,
      readFile: async (path: string) => {
        const f = await this.vfs!.readFile(path);
        return f ? { content: f.content, size: f.size } : null;
      },
      writeFile: async (path: string, content: string | ArrayBuffer) => {
        await this.vfs!.writeFile(path, content, typeof content === 'string' ? 'json' : 'binary');
      },
      getGUID: (path: string) => this.getGUID(path),
      addProducedAsset: (produced) => {
        producedAssets.push({
          ...produced,
          producedAt: new Date(),
        });
      },
    };
    
    // Run importer
    try {
      // Clear old produced entries BEFORE running importer
      // (compilers may call registerProducedEntry directly during import)
      await this.clearProducedEntries(guid);
      
      log.trace('Running importer for:', entry.path);
      const result = await importer.import(context);
      log.trace('Import result:', result.success, 'artifacts:', result.artifacts?.length, 'produced:', producedAssets.length);
      
      // Merge produced assets from result and context
      const allProduced = [...producedAssets, ...(result.producedAssets || [])];
      
      if (result.success) {
        await this.updateEntry(guid, {
          importStatus: 'imported',
          importError: undefined,
          artifacts: result.artifacts,
          dependencies: result.dependencies,
          importedData: result.importedData,
        });
        
        // Register produced assets from context/result (those returned by importer)
        // Note: compilers may also call registerProducedEntry directly during import
        for (const produced of allProduced) {
          await this.registerProducedEntry(guid, produced.path, produced.type, {
            size: produced.size,
            metadata: produced.metadata,
          });
        }
      } else {
        await this.updateEntry(guid, {
          importStatus: 'failed',
          importError: result.error,
        });
      }
      
      // Notify listeners that import data changed
      this.notifyListeners([{ type: 'modified', guid, path: entry.path }]);
      
      return result;
    } catch (e) {
      const error = e instanceof Error ? e.message : String(e);
      await this.updateEntry(guid, {
        importStatus: 'failed',
        importError: error,
      });
      return { success: false, error };
    }
  }

  /**
   * Import asset by path
   */
  static async importAssetByPath(path: string, force: boolean = false): Promise<ImportResult> {
    this.ensureInitialized();
    
    const guid = this.db!.getGuidForPath(path);
    if (!guid) {
      // Index first, then import
      const entry = await this.indexFile(path);
      return this.importAsset(entry.guid, force);
    }
    
    return this.importAsset(guid, force);
  }

  /**
   * Import all pending assets
   * 
   * @param onProgress - Progress callback
   * @returns Number of assets imported
   */
  static async importPending(onProgress?: ProgressCallback): Promise<{ imported: number; failed: number }> {
    this.ensureInitialized();
    
    // Get all pending assets
    const result = await this.db!.query({});
    const pending = result.entries.filter(e => e.importStatus === 'pending');
    
    let imported = 0;
    let failed = 0;
    
    for (let i = 0; i < pending.length; i++) {
      const entry = pending[i];
      
      onProgress?.({
        current: i,
        total: pending.length,
        phase: 'indexing',
        currentItem: entry.path,
        message: `Importing ${entry.path}`,
      });
      
      const importResult = await this.importAsset(entry.guid);
      if (importResult.success) {
        imported++;
      } else {
        failed++;
      }
    }
    
    onProgress?.({
      current: pending.length,
      total: pending.length,
      phase: 'complete',
      message: `Imported ${imported}, failed ${failed}`,
    });
    
    return { imported, failed };
  }

  /**
   * Get all assets that need import
   */
  static async getPendingImports(): Promise<AssetEntry[]> {
    this.ensureInitialized();
    const result = await this.db!.query({});
    return result.entries.filter(e => e.importStatus === 'pending');
  }

  /**
   * Get all assets with failed imports
   */
  static async getFailedImports(): Promise<AssetEntry[]> {
    this.ensureInitialized();
    const result = await this.db!.query({});
    return result.entries.filter(e => e.importStatus === 'failed');
  }

  // ============================================
  // STATISTICS
  // ============================================

  /**
   * Get index statistics
   */
  static async getStats(): Promise<IndexStats> {
    this.ensureInitialized();
    return this.db!.getStats();
  }

  /**
   * Get total count
   */
  static async count(): Promise<number> {
    this.ensureInitialized();
    return this.db!.count();
  }

  // ============================================
  // CHANGE LISTENERS
  // ============================================

  /**
   * Subscribe to index changes
   */
  static onChange(listener: (changes: AssetChange[]) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private static notifyListeners(changes: AssetChange[]): void {
    for (const listener of this.listeners) {
      try {
        listener(changes);
      } catch (e) {
        log.error('Listener error:', e);
      }
    }
  }

  // ============================================
  // CLEAR
  // ============================================

  /**
   * Clear all indexed data
   */
  static async clear(): Promise<void> {
    this.ensureInitialized();
    await this.db!.clear();
    log.info('Index cleared');
  }

  /**
   * Clear all registrations (content types & importers)
   */
  static clearRegistrations(): void {
    this.contentTypes.clear();
    this.importers.clear();
    this.extensionToContentType.clear();
  }
}
